// What every view of a signed-in, unlocked browser shares: the account, whether the relay is reachable, the Library's
// projects (opened with the vault key), and a way to say the session has ended.
import { createContext, useContext, useEffect, useState } from 'react';
import type { Account } from '../store/account.ts';
import type { LibraryProject, PendingShare } from '../store/library.ts';

export interface AppState {
  account: Account;
  online: boolean;
  /** Null until the cached list (or the relay's) has been read. */
  projects: LibraryProject[] | null;
  /** Shares this account accepted that their owners haven't confirmed yet (W9a). */
  pendingShares: PendingShare[];
  /** The relay's list couldn't be fetched this time (offline, or unreachable). */
  listFromCache: boolean;
  refreshProjects(): Promise<void>;
  /** Records a project's words, title and last opening as the Library shows them. */
  noteProject(id: string): void;
  sessionEnded(): void;
  /** Profile ▸ Sign Out: the relay ends this browser's session, and its keys and copies of the projects go. */
  signOut(): Promise<void>;
}

export const AppContext = createContext<AppState | null>(null);

export function useApp(): AppState {
  const app = useContext(AppContext);
  if (!app) throw new Error('useApp outside the signed-in app');
  return app;
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine !== false);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Says something to screen readers through the page's live region. */
export function announce(text: string) {
  const live = document.getElementById('announce');
  if (!live) return;
  live.textContent = '';
  setTimeout(() => (live.textContent = text), 30);
}

/** Per-browser view choices (column visibility, panels): never state that matters, so failures are ignored. */
export function usePreference<T>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(`ws:${key}`);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = (v: T) => {
    setValue(v);
    try {
      localStorage.setItem(`ws:${key}`, JSON.stringify(v));
    } catch {
      // kept for this page only
    }
  };
  return [value, set];
}
